import { Box, Flex, Heading, List, ListItem } from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { groupBy, keyBy } from "lodash";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { assertPresence, getAssertedStringArray } from "utils/assertions";
import ColumnValues from "components/Shared/ColumnValues";
import Well from "components/Shared/Well";
import LinkButton, { LinkButtonProps } from "components/Shared/LinkButton";
import LoadingSpinner from "components/Shared/PageLoadingSpinner";
import EmptyPlaceholder from "components/Shared/EmptyPlaceholder";

function MappingsPageLink(props: LinkButtonProps) {
  return (
    <LinkButton
      rightIcon={<ChevronRightIcon boxSize="1.5em" />}
      variant="outline"
      colorScheme="lightPinkButton"
      size="xs"
      sx={{ ".chakra-button__icon": { ml: 0 } }}
      {...props}
    />
  );
}
const fragment = gql(/* GraphQL */ `
  fragment ReverseMappingsFragment on DataSourceTypeColumnValue {
    id
    columnValues
    definition {
      id
      columns
      dataSourceType {
        name
        fullLabel
      }
    }
  }
`);

type Props = {
  columnValues: FragmentType<typeof fragment>[] | undefined | null;
  getMappingPageLinkForType?: (dataSourceTypeName: string) => string;
  isLoading?: boolean;
  mappingPageLink?: string;
};

export default function ReverseMappings({
  columnValues: fragmentData,
  getMappingPageLinkForType,
  isLoading,
  mappingPageLink,
}: Props) {
  const dstcvs = getFragmentData(fragment, fragmentData);

  const grouped = groupBy(dstcvs, (r) => r.definition?.dataSourceType?.name);

  const definitionsByName = keyBy(
    dstcvs?.map((r) => {
      assertPresence(r.definition?.dataSourceType);
      return r.definition;
    }),
    (r) => r.dataSourceType!.name,
  );

  if (!isLoading && dstcvs?.length === 0) {
    return (
      <Box p={6}>
        <EmptyPlaceholder as={Box} opacity={0.5} textAlign="center">
          No mappings found.
        </EmptyPlaceholder>

        {mappingPageLink && (
          <Box textAlign="center" mt={3}>
            <MappingsPageLink href={mappingPageLink}>
              Manage Mappings
            </MappingsPageLink>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      <List>
        {Object.keys(grouped).map((dataSourceTypeName) => {
          const mappingPageLinkForType =
            getMappingPageLinkForType?.(dataSourceTypeName);

          return (
            <ListItem
              key={dataSourceTypeName}
              my={6}
              _first={{ mt: 0 }}
              _last={{ mb: 0 }}
            >
              <Flex gap={4}>
                <Heading size="xs" color="magenta.veryDark" m={0}>
                  {
                    definitionsByName[dataSourceTypeName]?.dataSourceType
                      ?.fullLabel
                  }
                </Heading>
                {mappingPageLinkForType && (
                  <MappingsPageLink href={mappingPageLinkForType} ml="auto">
                    Mappings
                  </MappingsPageLink>
                )}
              </Flex>

              <List>
                {grouped[dataSourceTypeName].map((dstcv) => {
                  assertPresence(dstcv.definition);
                  return (
                    <ListItem key={dstcv.id} my={2}>
                      <Well
                        backgroundColor="gray.200"
                        fontSize="xs"
                        py=".5em"
                        borderRadius="md"
                      >
                        <ColumnValues
                          columns={getAssertedStringArray(
                            dstcv.definition.columns,
                          )}
                          valuesByColumn={dstcv.columnValues}
                          labelProps={{
                            width: "13em",
                            pr: ".75em",
                            textAlign: "right",
                            textTransform: "none",
                            isTruncated: true,
                          }}
                          cellProps={{ py: ".15em", lineHeight: "normal" }}
                          layout="fixed"
                        />
                      </Well>
                    </ListItem>
                  );
                })}
              </List>
            </ListItem>
          );
        })}
      </List>

      {isLoading && <LoadingSpinner p={6} />}
    </>
  );
}
